<template>
  <div>
    <div
      v-if="loading"
      class="tw:mt-10 tw:flex tw:w-full tw:justify-center">
      <Spinner
        size="md"
        class="tw:loading-md" />
    </div>
    <div v-else>
      <div
        v-if="showError"
        class="tw:fixed tw:top-4 tw:right-4 tw:z-50 tw:max-w-lg tw:min-w-80">
        <Alert
          type="error"
          closable
          @close="showError = false">
          <span data-testid="error">{{ error }}</span>
        </Alert>
      </div>

      <PolicyDetails
        title="Select Policies"
        scenario="program"
        :showExpiredAtStart="false"
        :hideDownload="true"
        :showOnlyMoreFiltersSection="true"
        extraColumnTitle="Selection"
        :preloadedPolicies="policies"
        :showWithoutProgramToggle="true"
        :showWithoutProgram="true"
        :key="rerenderKey"
        @policy-details-refresh="refreshPoliciesWithOptions">
        <template v-slot:inner>
          <form @submit.prevent="">
            <div class="form-container">
              <h1>{{ isEditMode ? 'Edit Program' : 'Create New Program' }}</h1>
              <div class="form-controls tw:mt-4 tw:mb-6 tw:flex tw:flex-wrap tw:items-center tw:gap-4">
                <div class="text-entry tw:w-96 tw:shrink-0">
                  <TextField
                    class="text-input"
                    v-model="form.programName"
                    placeholder="Program Name"
                    label="Program Name"
                    :error="nameErrors.length > 0"
                    :error-message="nameErrors.join(' ')"
                    hint="Example: Property (Canada)"
                    data-testid="program-name"
                    @update:model-value="
                      () => {
                        v$.form.programName.$touch();
                        dirty = true;
                      }
                    " />
                </div>
                <div class="text-entry tw:w-36 tw:shrink-0">
                  <TextField
                    class="text-input"
                    v-model="form.year"
                    type="number"
                    placeholder="Program Year"
                    label="Program Year"
                    :error="yearErrors.length > 0"
                    :error-message="yearErrors.join(' ')"
                    hint="Example: 2022"
                    data-testid="program-year"
                    @update:model-value="
                      () => {
                        v$.form.year.$touch();
                        dirty = true;
                      }
                    " />
                </div>
                <AppButton
                  primary
                  :loading="saving"
                  data-testid="save-program"
                  @click="saveProgram()">
                  {{ isEditMode ? 'Save' : 'Create' }} Program
                </AppButton>
                <AppButton
                  class="tw:shrink-0 tw:rounded-full"
                  secondary
                  outline
                  data-testid="cancel-program"
                  @click="$router.push(`/account/${$route.params.id}/programs`)">
                  Cancel
                </AppButton>
              </div>
            </div>
          </form>
        </template>

        <template v-slot:extraColumn="{ policy }">
          <div class="tw:flex tw:content-center">
            <input
              type="checkbox"
              :checked="!!selectedPolicies[policy.uniqPolicy]"
              @click="togglePolicy(policy)"
              class="tw:border-opacity-50 tw:border-gray-primary tw:checkbox tw:mx-auto tw:rounded-md tw:border-1 tw:border-solid tw:checkbox-sm tw:checkbox-primary" />
          </div>
        </template>

        <template v-slot:extraColumnHeader>
          <div class="tw:flex tw:content-center">
            <input
              type="checkbox"
              :checked="checkboxState === 'checked'"
              :indeterminate="checkboxState === 'indeterminate'"
              class="tw:border-opacity-50 tw:border-gray-primary tw:checkbox tw:mx-auto tw:rounded-md tw:border-1 tw:border-solid tw:checkbox-sm tw:checkbox-primary"
              @click="toggleAllPolicies" />
          </div>
        </template>

        <template v-slot:filter-bar-left>
          <router-link
            :to="`/account/${$route.params.id}/programs`"
            color="primary"
            class="back-button tw:flex tw:items-center tw:gap-1 tw:pr-2 tw:transition-all tw:hover:bg-gray-100">
            <div class="tw:flex tw:items-center">
              <ChevronLeft :size="16" />
              Back
            </div>
          </router-link>
        </template>
      </PolicyDetails>
    </div>
  </div>
</template>

<script>
import PolicyDetails from '@/views/policy/PolicyDetails.vue';
import { numeric, required } from '@vuelidate/validators';
import { accountService, referenceService } from '@/services/api';
import { useVuelidate } from '@vuelidate/core';
import { ChevronLeft } from 'lucide-vue-next';
import AppButton from '@/components/v2/AppButton.vue';
import Alert from '@/components/v2/Alert.vue';
import TextField from '@/components/v2/TextField.vue';
import Spinner from '@/components/v2/Spinner.vue';

export default {
  components: { PolicyDetails, ChevronLeft, AppButton, Alert, TextField, Spinner },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  data: () => ({
    loading: false,
    dirty: false,
    rerenderKey: 0,
    saving: false,
    showError: false,
    error: '',
    form: {
      programName: '',
      year: undefined,
    },
    policies: [],
    coverageTypes: [],
    selectedPolicies: {},
    isEditable: true,
    copiedProgramID: undefined,
    showCurrentOnly: true,
    showExternal: false,
    showWithoutProgramOnly: false,
    mergedPrograms: undefined,
    checkboxState: '',
  }),
  props: {
    isEditMode: { required: false, type: Boolean, default: false },
  },
  validations: {
    form: {
      programName: {
        required,
      },
      year: {
        required,
        numeric,
      },
    },
  },
  computed: {
    nameErrors() {
      if (!this.v$?.form?.programName?.$dirty) {
        return [];
      }
      const errors = [];
      if (!this.v$.form?.programName?.required) errors.push('A program name is required.');
      return errors;
    },
    yearErrors() {
      if (!this.v$?.form?.year?.$dirty) {
        return [];
      }
      const errors = [];
      if (!this.v$.form?.year?.required) errors.push('A program year is required.');
      if (this.form.year !== undefined && !this.v$.form?.year?.numeric && isNaN(Number(this.form.year)))
        errors.push('Program year must be a number.');
      return errors;
    },
    programId() {
      if (this.isEditMode) {
        return this.$route.params.programId;
      } else {
        return '';
      }
    },
    allPoliciesSelected() {
      return Object.keys(this.selectedPolicies).length === this.policies.length;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!');
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  methods: {
    refreshPoliciesWithOptions(options) {
      this.showCurrentOnly = options.showCurrentOnly;
      this.showExternal = options.showExternal;
      this.showWithoutProgramOnly = options.showWithoutProgramOnly;

      this.loadPolicies();
    },
    togglePolicy(policy) {
      if (policy.isChildPolicy) {
        return;
      }

      if (this.selectedPolicies[policy.uniqPolicy]) {
        delete this.selectedPolicies[policy.uniqPolicy];
        this.selectedPolicies = { ...this.selectedPolicies };
      } else {
        this.selectedPolicies[policy.uniqPolicy] = policy;
        this.selectedPolicies = { ...this.selectedPolicies };
      }

      this.checkboxState =
        Object.keys(this.selectedPolicies).length === this.policies.length
          ? 'checked'
          : Object.keys(this.selectedPolicies).length > 0
            ? 'indeterminate'
            : '';
    },
    toggleAllPolicies() {
      if (Object.keys(this.selectedPolicies).length !== 0) {
        this.selectedPolicies = {};
        this.checkboxState = '';
      } else {
        for (const policy of this.policies) {
          this.togglePolicy(policy);
        }
        this.checkboxState = 'checked';
      }
    },
    async saveProgram() {
      if (!this.isEditable) {
        return;
      }
      this.v$.$touch();
      if (this.v$.$anyError || this.v$.$invalid) {
        return;
      }
      if (Object.values(this.selectedPolicies).length === 0) {
        return;
      }
      try {
        this.saving = true;
        // determine tower type
        // todo: this seems to be tower v1, probably can remove in the future
        let towerType = 'Ungrouped';
        const policies = Object.values(this.selectedPolicies);
        if (!this.isEditMode) {
          await accountService.createProgram(this.$route.params.id, {
            name: this.form.programName,
            year: this.form.year,
            towerType,
            PolicyIDs: policies.map((x) => Number(x?.uniqPolicy)),
          });
        } else {
          await accountService.updateProgram(this.$route.params.id, this.programId, {
            name: this.form.programName,
            year: this.form.year,
            towerType,
            PolicyIDs: policies.map((x) => Number(x?.uniqPolicy)),
          });
        }
        this.saving = false;
        this.dirty = false;
        this.$router.push(`/account/${this.$route.params.id}/programs`);
      } catch (e) {
        this.error = `An error has occurred. Please try again later!\n\nDetails: ${e}`;
        this.showError = true;
      }
    },
    parseRoute() {
      this.copiedProgramID = this.$route.query.copiedProgramID;
      this.mergedPrograms = this.$route.query.mergedPrograms?.split(',');
    },
    async loadPolicies() {
      this.policies = await accountService.policiesById(this.$route.params.id, !this.showCurrentOnly, this.showWithoutProgramOnly, this.showExternal);
    },
    async refreshPolicies() {
      this.loading = true;

      await this.loadPolicies();
      await this.loadCoverageTypes();
      if (this.isEditMode) {
        const details = await accountService.getProgramDetails(this.$route.params.id, this.programId);

        if (details.program.type === 'Automated' || details.program.type === 'EPIC') {
          this.isEditable = false;
          this.dirty = false;
          this.$router.push(`/account/${this.$route.params.id}/programs/${details.program.id}`);
        } else {
          this.isEditable = true;
        }

        this.fillForm(details, false);
      }

      if (this.copiedProgramID !== undefined) {
        const details = await accountService.getProgramDetails(this.$route.params.id, this.copiedProgramID);
        this.fillForm(details, `Copy of: ${details.program.name}`, false);
      }

      if (this.mergedPrograms !== undefined) {
        const programDetails = [];
        for (let programId of this.mergedPrograms) {
          programDetails.push(await accountService.getProgramDetails(this.$route.params.id, programId));
        }

        const policies = Array.from(
          programDetails
            .map((x) => x.policies)
            .flat()
            .reduce((acc, curr) => {
              return acc.set(curr.uniqPolicy, curr);
            }, new Map())
            .values(),
        );

        const programNames = programDetails.map((x) => x.program.name);

        this.fillForm({ program: null, policies }, `Merged from: ${programNames.join(', ')}`, true);
      }

      this.loading = false;
    },
    fillForm(details, name, merged) {
      this.form.programName = name || details.program.name;

      if (!merged) {
        this.form.year = details.program.year;
      }

      for (const policy of details.policies) {
        this.selectedPolicies[policy.uniqPolicy] = policy;
      }
      this.selectedPolicies = { ...this.selectedPolicies };
      this.rerenderKey = this.rerenderKey + 1;
    },
    async loadCoverageTypes() {
      this.coverageTypes = (await referenceService.coverageTypes()).map((x) => x.label);
    },
  },
  mounted() {
    this.parseRoute();
    this.refreshPolicies();
  },
};
</script>

<style scoped>
.text-entry {
  display: block;
}
</style>
